// src/pages/Ranking.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { apiFetch } from "../lib/api";
import "./Ranking.css";
import { getAuth, onAuthStateChanged } from "firebase/auth";

/* ------------------------------ Hooks ------------------------------------ */

function useLeaderboard(scopeInitial = "seasonal") {
  const [scope, setScope] = useState(scopeInitial); // "seasonal" | "all_time"
  const [seasonIdRaw, setSeasonIdRaw] = useState(getCurrentSeasonId());
  const seasonId = useMemo(() => clampSeason(seasonIdRaw), [seasonIdRaw]); // prevent future seasons

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // token to ignore stale responses
  const reqTokenRef = useRef(0);

  // Build the query on demand; allow overriding the cursor (null on reset)
  const buildQuery = useCallback(
    (cursorOverride) => {
      const params = new URLSearchParams();
      params.set("scope", scope);
      if (scope === "seasonal") params.set("seasonId", seasonId);
      const c = cursorOverride === undefined ? cursor : cursorOverride;
      if (c) params.set("cursor", c);
      return `/api/leaderboard?${params.toString()}`;
    },
    [scope, seasonId, cursor]
  );

  const fetchPage = useCallback(
    async ({ reset = false } = {}) => {
      const myToken = ++reqTokenRef.current;
      try {
        setLoading(true);
        setErr(null);

        const q = buildQuery(reset ? null : undefined);
        const data = await apiFetch(q);

        if (myToken !== reqTokenRef.current) return; // stale

        setItems((prev) => {
          const merged = reset ? data.items : [...prev, ...dedupe(prev, data.items)];
          return sortByPointsDesc(merged);
        });

        setCursor(data.nextCursor || null);
      } catch (e) {
        if (myToken !== reqTokenRef.current) return; // stale
        setErr(e?.message || "Failed to load leaderboard");
      } finally {
        if (myToken !== reqTokenRef.current) return; // stale
        setLoading(false);
      }
    },
    [buildQuery]
  );

  useEffect(() => {
    reqTokenRef.current++; // invalidate any in-flight request
    setItems([]);
    setCursor(null);
    fetchPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, seasonId]);

  return {
    scope,
    setScope,
    seasonId,
    setSeasonId: (val) => setSeasonIdRaw(clampSeason(val)),
    items,
    cursor,
    loading,
    err,
    fetchMore: () => fetchPage({ reset: false }),
    refresh: () => fetchPage({ reset: true }),
  };
}

/* ------------------------------ Auth helpers ------------------------------ */

async function waitForAuthUser(timeoutMs = 5000) {
  const auth = getAuth();
  // If already available, return immediately
  if (auth.currentUser) return auth.currentUser;

  // Wait for the first auth state (initialized or signed-in)
  return new Promise((resolve) => {
    let done = false;
    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        resolve(auth.currentUser || null);
      }
    }, Math.max(0, timeoutMs));
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!done) {
        done = true;
        clearTimeout(timer);
        unsub();
        resolve(user || null);
      }
    });
  });
}

/* ------------------------------ My Rank ----------------------------------- */

function useMyRank(scope, seasonId) {
  const [data, setData] = useState(null);   // {scope, points, rank}
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [unauth, setUnauth] = useState(false);

  const rankReqRef = useRef(0);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (scope === "all_time") params.set("season", "all");
    else params.set("season", seasonId);
    return `/api/leaderboard/me?${params.toString()}`;
  }, [scope, seasonId]);

  const run = useCallback(async () => {
    const myToken = ++rankReqRef.current;
    setErr(null);
    setUnauth(false);
    setLoading(true);
    try {
      // ✅ Ensure Firebase auth is initialized and we actually have a user
      const user = await waitForAuthUser(6000);
      if (myToken !== rankReqRef.current) return; // stale

      // Not signed in at all
      if (!user) {
        setData(null);
        setUnauth(true);
        return;
      }

      // 🔒 Anonymous (guest) session — do not call API; just show login prompt
      if (user.isAnonymous) {
        setData(null);
        setUnauth(true);
        return;
      }

      // Use modular SDK to get a fresh token
      const getToken = async () => user.getIdToken(true);

      // Let apiFetch attach our token via the provided getter
      const res = await apiFetch(query, { requireAuth: true, getToken });

      if (myToken !== rankReqRef.current) return; // stale
      setData(res);
    } catch (e) {
      if (myToken !== rankReqRef.current) return; // stale
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        // Token rejected by backend (e.g., wrong project/audience)
        setData(null);
        setUnauth(true);
        return;
      }
      setErr(e?.message || "Failed to load your rank");
    } finally {
      if (myToken !== rankReqRef.current) return; // stale
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    rankReqRef.current++;
    run();
  }, [run]);

  return { data, loading, err, unauth, refresh: run };
}

/* ------------------------------ Page ------------------------------------- */

export default function Ranking() {
  // Track auth to tailor the message in the banner (guest vs signed out)
  const [authChecked, setAuthChecked] = useState(false);
  const [isAnon, setIsAnon] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => {
      setIsAnon(!!u && u.isAnonymous === true);
      setAuthChecked(true);
    });
    return unsub;
  }, []);

  const lb = useLeaderboard("seasonal");
  const me = useMyRank(lb.scope, lb.scope === "seasonal" ? lb.seasonId : null);

  return (
    <div className="ranking-page">
      <header className="ranking-header">
        <h1 className="ranking-title">🏆 Leaderboard</h1>
        <ScopeToggle scope={lb.scope} onChange={lb.setScope} />
        {lb.scope === "seasonal" && (
          <SeasonPicker seasonId={lb.seasonId} onChange={lb.setSeasonId} />
        )}
      </header>

      <section className="my-rank-section" aria-live="polite">
        <MyRankCard scope={lb.scope} seasonId={lb.seasonId} me={me} onRetry={me.refresh} isAnon={isAnon} />
      </section>

      {lb.err && (
        <div className="error-box">
          {lb.err} <button onClick={lb.refresh} className="btn-link">Retry</button>
        </div>
      )}

      <div className="lb-list-container">
        <ol className="lb-list">
          {lb.items.map((u, idx) => (
            <li
              key={u.id || u.firebaseUid}
              className={`lb-item ${idx + 1 <= 3 ? `rank-${idx + 1}` : ""}`}
            >
              <div className="lb-item-left">
                <RankBadge n={idx + 1} />
                <div className="lb-item-user">
                  <div className="lb-username">
                    {u.username || u.displayName || shortId(u.firebaseUid)}
                  </div>
                  <div className="lb-meta">
                    {u.countryCode ? `${flagEmoji(u.countryCode)} ${u.countryCode}` : "🌍"} · W {u.wins ?? 0} / L {u.losses ?? 0}
                  </div>
                </div>
              </div>
              <div className="lb-points">{fmtPoints(u.points)} pts</div>
            </li>
          ))}
          {lb.loading && <SkeletonRows count={3} />}
          {!lb.loading && lb.items.length === 0 && !lb.err && (
            <EmptyState label="No players yet. Be the first to score points!" />
          )}
        </ol>
      </div>

      <div className="lb-footer">
        {lb.cursor ? (
          <button onClick={lb.fetchMore} disabled={lb.loading} className="btn">
            {lb.loading ? "Loading…" : "Load more"}
          </button>
        ) : (
          !lb.loading && lb.items.length > 0 && <div className="end-of-list">End of leaderboard</div>
        )}
      </div>
    </div>
  );
}

/* ----------------------------- UI helpers ---------------------------------- */

function ScopeToggle({ scope, onChange }) {
  return (
    <div role="group" aria-label="scope toggle" className="scope-toggle">
      <button
        onClick={() => onChange("seasonal")}
        className={`chip ${scope === "seasonal" ? "chip--active" : ""}`}
      >
        Season
      </button>
      <button
        onClick={() => onChange("all_time")}
        className={`chip ${scope === "all_time" ? "chip--active" : ""}`}
      >
        All-time
      </button>
    </div>
  );
}

function SeasonPicker({ seasonId, onChange }) {
  const current = getCurrentSeasonId();
  const canPrev = true;
  const canNext = seasonId < current;

  const prev = () => onChange(shiftSeason(seasonId, -1));
  const next = () => onChange(shiftSeason(seasonId, +1));

  return (
    <div className="season-picker">
      <button className="mini-btn" onClick={prev} disabled={!canPrev} aria-label="Previous month">
        ‹
      </button>
      <div className="season-current">{seasonId}</div>
      <button className="mini-btn" onClick={next} disabled={!canNext} aria-label="Next month">
        ›
      </button>
    </div>
  );
}

function MyRankCard({ scope, seasonId, me, onRetry, isAnon }) {
  if (me.loading) {
    return (
      <div className="banner">
        <div className="banner-title">Your Rank</div>
        <div className="banner-subtle">Loading…</div>
      </div>
    );
  }
  if (me.unauth) {
    const label = scope === "all_time" ? "All-time" : `Season ${seasonId}`;
    const note = isAnon
      ? "End guest session and log in to see your rank."
      : "Sign in to see your personal rank";
    return (
      <div className="banner">
        <div className="banner-row">
          <div className="banner-title">Your Rank</div>
          <div className="banner-subtle">{label}</div>
        </div>
        <div className="banner-note">{note}</div>
      </div>
    );
  }
  if (me.err) {
    return (
      <div className="banner">
        <div className="banner-title">Your Rank</div>
        <div className="banner-error">
          {me.err} <button onClick={onRetry} className="btn-link">Retry</button>
        </div>
      </div>
    );
  }
  if (!me.data) return null;

  const label = scope === "all_time" ? "All-time" : `Season ${seasonId}`;
  return (
    <div className="banner">
      <div className="banner-row">
        <div className="banner-title">Your Rank</div>
        <div className="banner-subtle">{label}</div>
      </div>
      <div className="banner-stats">
        <span className="banner-rank">#{me.data.rank ?? "—"}</span>
        <span className="dot">•</span>
        <span>{fmtPoints(me.data.points ?? 0)} pts</span>
      </div>
    </div>
  );
}

function RankBadge({ n }) {
  const medal = n === 1 ? "🥇" : n === 2 ? "🥈" : n === 3 ? "🥉" : "🔹";
  return <div className="rank-badge">{medal} {n}.</div>;
}

function SkeletonRows({ count = 3 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton-row" />
      ))}
    </>
  );
}

function EmptyState({ label }) {
  return <div className="empty-state">{label}</div>;
}

/* --------------------------- tiny utilities -------------------------------- */

function getCurrentSeasonId(d = new Date()) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}
function clampSeason(seasonId) {
  const cur = getCurrentSeasonId();
  if (!/^\d{4}-\d{2}$/.test(String(seasonId || ""))) return cur;
  return seasonId > cur ? cur : seasonId;
}
function shiftSeason(seasonId, delta) {
  const [y, m] = seasonId.split("-").map((v) => parseInt(v, 10));
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  const next = getCurrentSeasonId(d);
  return clampSeason(next);
}
function shortId(s) {
  if (!s) return "User";
  const str = String(s);
  return str.length <= 8 ? str : str.slice(0, 4) + "…" + str.slice(-3);
}
function fmtPoints(n) {
  const v = Number(n || 0);
  if (v >= 1000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  return String(v);
}
function flagEmoji(code) {
  try {
    const cc = String(code || "").trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(cc)) return "🌍";
    const A = 0x1f1e6;
    const a = cc.charCodeAt(0) - 65;
    const b = cc.charCodeAt(1) - 65;
    return String.fromCodePoint(A + a) + String.fromCodePoint(A + b);
  } catch {
    return "🌍";
  }
}
function dedupe(prev, next) {
  const seen = new Set(prev.map((r) => r.id || r.firebaseUid));
  return next.filter((r) => {
    const key = r.id || r.firebaseUid;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
function sortByPointsDesc(list) {
  const copy = list.slice();
  copy.sort((a, b) => {
    const pa = Number(a.points || 0);
    const pb = Number(b.points || 0);
    if (pb !== pa) return pb - pa;
    const ua = (a.username || a.displayName || a.firebaseUid || "").toString();
    const ub = (b.username || b.displayName || b.firebaseUid || "").toString();
    if (ua && ub && ua !== ub) return ua.localeCompare(ub);
    const ia = (a.id || a._id || a.firebaseUid || "").toString();
    const ib = (b.id || b._id || b.firebaseUid || "").toString();
    return ia.localeCompare(ib);
  });
  return copy;
}
